import React, { useState } from 'react';
import { Zap, Play, Clock, CheckCircle2, Loader2 } from 'lucide-react';

const MockConversionSimulator = ({ project }) => {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const runMockConversion = () => {
    setRunning(true);
    setTimeout(() => {
      setResults({
        start_time: new Date().toISOString(),
        end_time: new Date(Date.now() + 8.5 * 60 * 60 * 1000).toISOString(),
        total_duration_hours: 8.5,
        phases: [
          { name: 'System Backup', duration_min: 90, status: 'completed', issues: 0 },
          { name: 'SUM Preparation', duration_min: 120, status: 'completed', issues: 0 },
          { name: 'DMO Execution', duration_min: 300, status: 'completed', issues: 2 },
          { name: 'Post-Processing', duration_min: 60, status: 'completed', issues: 0 }
        ],
        issues_found: [
          { phase: 'DMO', issue: 'Large table BSEG took longer than expected', recommendation: 'Consider table partitioning' },
          { phase: 'DMO', issue: 'Memory spike during ACDOCA conversion', recommendation: 'Increase system memory for production run' }
        ],
        success: true,
        recommendations_for_production: [
          'Allocate additional 32GB memory for DMO phase',
          'Schedule conversion during low-usage window',
          'Pre-archive BSEG data older than 7 years',
          'Test rollback procedure before production'
        ]
      });
      setRunning(false);
    }, 4000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">Mock Conversion Simulator</h2>
            <p className="text-gray-600">Run full conversion simulation to predict production behavior</p>
          </div>
          <button onClick={runMockConversion} disabled={running} className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl flex items-center space-x-2 disabled:opacity-50">
            {running ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Running...</span></> : <><Play className="w-5 h-5" /><span>Run Mock</span></>}
          </button>
        </div>
      </div>

      {results && (
        <>
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">Mock Conversion {results.success ? 'Successful' : 'Failed'}</h3>
                <p className="text-green-100">Duration: {results.total_duration_hours} hours</p>
              </div>
              <CheckCircle2 className="w-16 h-16 opacity-80" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold mb-4">Phase Execution</h3>
            <div className="space-y-3">
              {results.phases.map((phase, i) => (
                <div key={i} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold">{phase.name}</h4>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">{phase.duration_min} min</p>
                      {phase.issues > 0 && <p className="text-xs text-red-600">{phase.issues} issues</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-xl font-bold mb-4">Production Recommendations</h3>
            <ul className="space-y-2">
              {results.recommendations_for_production.map((rec, i) => (
                <li key={i} className="flex items-start space-x-2 text-gray-700">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default MockConversionSimulator;